// Catalog.ID Client — Organization Workspace Actions
// ============================================================================
// Role-gated actions for the Organization workspace: rotate keys, revalidate
// domain, flatten the key chain, terminate the organization. They are the
// organization's own verbs, so they ride the org-claims pane's title row
// (see org-claims-pane.ts) instead of the shell's global action bar — a
// top-bar action gated on the focused pane type is a title-row action in
// disguise (shell USAGE.md "Where a button belongs").

import { createElement } from '@catalog-shell';
import type { ShellAPI, PaneActionDescriptor } from '@catalog-shell';
import { identity } from '../state/identity';
import * as organizationApi from '../api/organization';
import * as delegateApi from '../api/delegate';
import * as accountApi from '../api/account';
import { kemKeygen } from '../crypto/kem';
import { wrapPrivateKey, unwrapPrivateKey, reEncryptClaimKeyWithChain } from '../crypto/claims';
import { encryptKeyBag, decryptKeyBagWithKey } from '../crypto/keybag';
import { buildRotatedOrgBag, planRotationWraps } from './org-key-rotation';
import { createSupersecretTextarea } from '../components/supersecret-textarea';
import * as aes from '../crypto/aes';
import { keys } from '../state/keys';
import { signOrgStepUp, signStepUp } from '../auth/step-up';
import { liveAgentsMessage, withAgentAcknowledgement } from '../auth/principal-termination';
import { loadOrgSigningSeed } from '../ceremony/signer';

/**
 * The organization's workspace verbs, as title-row descriptors. All four fold
 * into the trailing ⋯ menu: they are rare, deliberate, org-wide operations,
 * not per-row verbs. An action the caller's roles do not allow is OMITTED
 * rather than disabled — the server gates every call independently anyway.
 */
export function orgWorkspaceActions(shell: ShellAPI): PaneActionDescriptor[] {
    const actions: PaneActionDescriptor[] = [];
    const canManageKeys = identity.hasRole('owner') || identity.hasRole('admin');

    // Rotate Organization Keys — owner or admin
    if (canManageKeys) {
        actions.push({
            key: 'org.rotate-keys',
            label: 'Rotate Keys',
            overflow: true,
            execute: () => void showRotateKeysModal(shell),
        });
    }

    // Revalidate Domain — owner or admin
    if (canManageKeys) {
        actions.push({
            key: 'org.revalidate-domain',
            label: 'Revalidate Domain',
            overflow: true,
            execute: async () => {
                const orgCid = identity.getOrganizationCid();
                if (!orgCid) return;
                const ok = await shell.confirm({
                    title: 'Revalidate Domain',
                    message: 'Initiate domain ownership revalidation? You will need to update .well-known/catalogid.txt with a new token.',
                    confirmLabel: 'Revalidate',
                });
                if (!ok) return;
                try {
                    await organizationApi.revalidateDomain(orgCid);
                    shell.toast('Domain revalidation initiated', 'success');
                } catch (err) {
                    shell.toast(err instanceof Error ? err.message : 'Revalidation failed', 'error');
                }
            },
        });
    }

    // Flatten Key Chain — owner only. Per WP §3a.3.1, collapses claim-key
    // envelope chains back to depth 1. Peer of Rotate Keys: both are
    // admin-level org key management.
    if (identity.hasRole('owner')) {
        actions.push({
            key: 'org.flatten-keys',
            label: 'Flatten Key Chain',
            overflow: true,
            execute: () => void showFlattenKeysModal(shell),
        });
    }

    // Restore Organization — owner only (docs/cid-governance.md M15): a locked
    // organization, or the frozen one bound to this node's code, comes back by a
    // step-up signed with the ORGANIZATION's key, which only owners hold. It
    // needs no operator: for the bound organization the operator review is
    // exactly what its lock or freeze took away.
    if (identity.hasRole('owner')) {
        actions.push({
            key: 'org.restore',
            label: 'Unlock or Unfreeze Organization',
            overflow: true,
            execute: () => void restoreOrganization(shell),
        });
    }

    // Terminate Organization — owner only
    if (identity.hasRole('owner')) {
        actions.push({
            key: 'org.terminate',
            label: 'Terminate Organization',
            overflow: true,
            variant: 'danger',
            execute: () => void showTerminateOrgModal(shell),
        });
    }

    return actions;
}

// ============================================================================
// Restore Organization (docs/cid-governance.md M15)
// ============================================================================

async function restoreOrganization(shell: ShellAPI): Promise<void> {
    const orgCid = identity.getOrganizationCid();
    if (!orgCid) return;

    let status: string;
    try {
        status = await organizationApi.getOrganizationStanding(orgCid);
    } catch (err) {
        shell.toast(err instanceof Error ? err.message : 'Could not read the organization\'s status', 'error');
        return;
    }
    if (status !== 'locked' && status !== 'frozen') {
        shell.toast(`The organization is ${status}; there is nothing to unlock or unfreeze.`, 'info');
        return;
    }

    const unlock = status === 'locked';
    const ok = await shell.confirm({
        title: unlock ? 'Unlock Organization' : 'Unfreeze Organization',
        message: unlock
            ? 'The organization is locked: its members can only read, and it holds no operator powers. '
                + 'Unlock it with the organization\'s own signing key, which you hold as an owner?'
            : 'The organization is frozen. Unfreeze it with the organization\'s own signing key? '
                + 'Only the organization bound to this node\'s operator code is unfrozen this way; '
                + 'any other lifts a lapsed domain\'s freeze by revalidating the domain.',
        confirmLabel: unlock ? 'Unlock' : 'Unfreeze',
    });
    if (!ok) return;

    const loaded = await loadOrgSigningSeed('wrapped-org-key', 'main_signing_key');
    if (!loaded.ok) {
        shell.toast(loaded.message, 'error');
        return;
    }
    try {
        const signature = await signOrgStepUp(unlock ? 'org-unlock' : 'org-unfreeze', orgCid, loaded.secretSeed);
        if (unlock) await organizationApi.unlockOrganization(orgCid, signature);
        else await organizationApi.unfreezeOrganization(orgCid, signature);
        shell.toast(unlock ? 'Organization unlocked' : 'Organization unfrozen', 'success');
        shell.emit('data-changed', { type: 'delegates' });
    } catch (err) {
        shell.toast(err instanceof Error ? err.message : 'The organization could not be restored', 'error');
    } finally {
        loaded.secretSeed.fill(0);
    }
}

// ============================================================================
// Terminate Organization Modal
// ============================================================================

async function showTerminateOrgModal(shell: ShellAPI): Promise<void> {
    const orgCid = identity.getOrganizationCid();
    if (!orgCid) return;

    const content = createElement('div', { className: 'cid-wizard-step' });
    content.appendChild(createElement('h3', {
        className: 'cid-wizard-heading',
        textContent: 'Terminate Organization',
    }));

    content.appendChild(createElement('p', {
        className: 'cid-wizard-text',
        textContent: 'This will permanently terminate the organization and ALL delegate identities linked to it. '
            + 'Every delegate username is permanently retired. All encrypted claims, keys, devices, and '
            + 'validation certificates will be destroyed. This cannot be undone.',
    }));

    // Public claims checkbox — GDPR erasure opt-in
    const eraseLabel = createElement('label', {
        style: { display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', marginTop: '12px' },
    });
    const eraseCheckbox = document.createElement('input');
    eraseCheckbox.type = 'checkbox';
    eraseCheckbox.style.marginTop = '3px';
    eraseLabel.appendChild(eraseCheckbox);
    eraseLabel.appendChild(createElement('span', {
        textContent: 'Also delete all public claims (full GDPR erasure)',
    }));
    content.appendChild(eraseLabel);

    content.appendChild(createElement('p', {
        className: 'cid-wizard-text',
        style: { opacity: '0.7', fontSize: '0.85em', marginTop: '4px' },
        textContent: 'By default, published public claims (including the organization\'s domain) are preserved '
            + 'under the perpetual publication license so that historical attributions remain resolvable.',
    }));

    // CID confirmation input
    const confirmLabel = createElement('label', {
        textContent: `Type the organization CID (${orgCid}) to confirm`,
    });
    confirmLabel.style.display = 'block';
    confirmLabel.style.marginTop = '16px';
    confirmLabel.style.marginBottom = '4px';
    content.appendChild(confirmLabel);

    const confirmInput = document.createElement('input');
    confirmInput.type = 'text';
    confirmInput.placeholder = orgCid;
    confirmInput.className = 'cid-auth-input';
    confirmInput.style.width = '100%';
    content.appendChild(confirmInput);

    const errorEl = createElement('div', { className: 'cid-pane-error' });
    content.appendChild(errorEl);

    const terminateBtn = createElement('button', {
        className: 'cs-btn cs-btn--no',
        textContent: 'Terminate Organization',
    });
    terminateBtn.style.marginTop = '16px';

    terminateBtn.addEventListener('click', async () => {
        if (confirmInput.value.trim() !== orgCid) {
            errorEl.textContent = 'CID does not match. Please type the exact organization CID to confirm.';
            errorEl.classList.add('cid-pane-error--visible');
            return;
        }

        terminateBtn.setAttribute('disabled', '');
        terminateBtn.textContent = 'Terminating...';
        errorEl.classList.remove('cid-pane-error--visible');

        try {
            // Irreversible: a signature by the owner's own key (docs/cid-governance.md
            // M15), and the delegates' live agents end with it only once the owner
            // agrees. The node refuses the organization bound to its own code.
            const done = await withAgentAcknowledgement(async (terminateAgents) => {
                const challengeSignature = await signStepUp('org-terminate');
                await organizationApi.terminateOrganization(orgCid, {
                    erasePublicClaims: eraseCheckbox.checked, challengeSignature, terminateAgents,
                });
            }, (agents) => shell.confirm({
                title: 'Terminate the Delegates\' Agents',
                message: liveAgentsMessage(agents, 'A delegate of this organization is'),
                confirmLabel: 'Terminate Organization and Agents',
                confirmVariant: 'no',
                requireExplicitChoice: true,
            }));
            if (!done) {
                terminateBtn.removeAttribute('disabled');
                terminateBtn.textContent = 'Terminate Organization';
                return;
            }
            shell.toast('Organization terminated', 'success');
            modal.close();
            window.location.href = '/';
        } catch (err) {
            terminateBtn.removeAttribute('disabled');
            terminateBtn.textContent = 'Terminate Organization';
            errorEl.textContent = err instanceof Error ? err.message : 'Termination failed';
            errorEl.classList.add('cid-pane-error--visible');
        }
    });

    content.appendChild(terminateBtn);
    const modal = shell.showModal(content);
}

// ============================================================================
// Rotate Keys Modal
// ============================================================================

async function showRotateKeysModal(shell: ShellAPI): Promise<void> {
    const orgCid = identity.getOrganizationCid();
    if (!orgCid) return;

    const content = createElement('div', { className: 'cid-wizard-step' });
    content.appendChild(createElement('h3', {
        className: 'cid-wizard-heading',
        textContent: 'Rotate Organization Keys',
    }));

    content.appendChild(createElement('p', {
        className: 'cid-wizard-text',
        textContent: 'After a delegate removal, the organization\'s encryption keys must be rotated to revoke the departed delegate\'s access. '
            + 'This generates new keypairs, re-wraps all claim keys, and redistributes wrapped keys to remaining delegates. '
            + 'The organization supersecret is required to update the key bag.',
    }));

    // Supersecret input
    const ssLabel = createElement('label', { textContent: 'Organization Supersecret' });
    ssLabel.style.display = 'block';
    ssLabel.style.marginTop = '12px';
    ssLabel.style.marginBottom = '4px';
    content.appendChild(ssLabel);

    const ss = createSupersecretTextarea({ placeholder: 'Enter organization supersecret' });
    content.appendChild(ss.element);

    const statusEl = createElement('div', { className: 'cid-trust-success' });
    const errorEl = createElement('div', { className: 'cid-pane-error' });
    content.appendChild(errorEl);
    content.appendChild(statusEl);

    const rotateBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes',
        textContent: 'Rotate Keys',
    }) as HTMLButtonElement;
    rotateBtn.style.marginTop = '12px';
    rotateBtn.disabled = true;
    ss.onValidityChange(valid => { rotateBtn.disabled = !valid; });

    rotateBtn.addEventListener('click', async () => {
        if (!ss.isValid()) return;
        const orgSupersecret = ss.getValue();

        rotateBtn.setAttribute('disabled', '');
        errorEl.classList.remove('cid-pane-error--visible');
        statusEl.textContent = 'Generating new keypairs...';

        try {
            // 1. The organization's FULL bag, decrypted with its supersecret — the
            //    organization's own record of what it holds (§16.6, G10), and the
            //    source of BOTH halves of the rotation below.
            statusEl.textContent = 'Reading the organization key bag...';
            const orgBagResponse = await organizationApi.getOrgKeyBag(orgCid);
            let serverOrgBag;
            try {
                ({ bag: serverOrgBag } = await decryptKeyBagWithKey(orgBagResponse, orgSupersecret));
            } catch {
                throw new Error('Invalid organization supersecret — could not decrypt the organization key bag');
            }

            // 2. New hybrid KEM keypairs for attr_encrypt and msg_encrypt, one
            //    generation above the highest the organization holds. The
            //    chain-walking convention (higher keyIndex = newer / outer envelope)
            //    must match the server's KeyRotationService and its accountpublickey
            //    index assignment (newAttrIndex = max(existing) + 1).
            statusEl.textContent = 'Generating new keypairs...';
            const newAttrKP = kemKeygen();
            const newMsgKP = kemKeygen();
            const maxIndex = (keyId: string) => serverOrgBag.entries
                .filter(e => e.keyId === keyId)
                .reduce((m, e) => Math.max(m, e.keyIndex ?? 0), -1);
            const newAttrIndex = maxIndex('attr_encrypt') + 1;
            const newMsgIndex = maxIndex('msg_encrypt') + 1;

            // 3. The new bag: the two new generations plus EVERY entry the
            //    organization held — /rotate is delete-all + create on the bag, so
            //    the submitted bag is the whole truth.
            const rotatedBag = buildRotatedOrgBag(
                serverOrgBag,
                aes.toBase64(newAttrKP.secretKey),
                aes.toBase64(newMsgKP.secretKey),
                newAttrIndex,
                newMsgIndex,
            );

            // 4. The wrapped set, planned from that same full bag (docs/cid-governance.md
            //    M15): each remaining delegate receives every generation its roles
            //    entitle it to — new and old attr/msg, and the keys that do not
            //    rotate (main_signing_key, xrpl_master, bitcash_wallet, the chat and
            //    mail roots). Never from the rotating delegate's own wrapped copies:
            //    an admin holds none of main_signing_key, xrpl_master or
            //    bitcash_wallet, and its rotation stripped them from their holders.
            statusEl.textContent = 'Fetching remaining delegates...';
            const delegates = await organizationApi.listDelegates(orgCid);
            const activeDelegates = delegates.filter(d => d.status === 'active' || d.status === 'partial');
            const wrapKeyOf = new Map<string, Uint8Array>();
            for (const del of activeDelegates) {
                const delAttrPub = (await accountApi.getPublicKeys(del.cid)).find(k => k.keyId === 'attr_encrypt');
                if (delAttrPub) wrapKeyOf.set(del.cid, aes.fromBase64(delAttrPub.publicKey));
            }

            statusEl.textContent = 'Wrapping keys for delegates...';
            const wrappedKeys: { delegateCid: string; keyId: string; keyIndex: number; wrappedKey: string }[] = [];
            const plan = planRotationWraps(rotatedBag, activeDelegates
                .filter(d => wrapKeyOf.has(d.cid))
                .map(d => ({ cid: d.cid, roles: d.roles ?? [] })));
            for (const w of plan) {
                wrappedKeys.push({
                    delegateCid: w.delegateCid,
                    keyId: w.keyId,
                    keyIndex: w.keyIndex,
                    wrappedKey: await wrapPrivateKey(w.privateKey, wrapKeyOf.get(w.delegateCid)!),
                });
            }

            statusEl.textContent = 'Encrypting key bag with org supersecret...';
            const encrypted = await encryptKeyBag(rotatedBag, orgSupersecret);

            statusEl.textContent = 'Submitting key rotation...';

            // 5. Submit to server
            const result = await organizationApi.rotateOrgKeys(orgCid, {
                newAttrEncryptPublicKey: aes.toBase64(newAttrKP.publicKey),
                newMsgEncryptPublicKey: aes.toBase64(newMsgKP.publicKey),
                publicKeyAlgorithm: 'X25519-MLKem1024',
                wrappedKeys,
                keyBagEntries: encrypted.entries,
                keySalt: encrypted.keySalt,
            });

            statusEl.textContent = `Keys rotated. ${result.claimKeysRewrapped} claim key(s) re-wrapped.`;
            shell.emit('data-changed', { type: 'claims' });

            // Reload to pick up new wrapped keys
            setTimeout(() => {
                modal.close();
                window.location.reload();
            }, 2000);

        } catch (e) {
            statusEl.textContent = '';
            errorEl.textContent = e instanceof Error ? e.message : 'Key rotation failed';
            errorEl.classList.add('cid-pane-error--visible');
            rotateBtn.removeAttribute('disabled');
        }
    });

    content.appendChild(rotateBtn);
    const modal = shell.showModal(content);
}

// ============================================================================
// Flatten Key Chain Modal (WP §3a.3.1)
// ============================================================================

async function showFlattenKeysModal(shell: ShellAPI): Promise<void> {
    const orgCid = identity.getOrganizationCid();
    if (!orgCid) return;

    const content = createElement('div', { className: 'cid-wizard-step' });
    content.appendChild(createElement('h3', {
        className: 'cid-wizard-heading',
        textContent: 'Flatten Key Chain',
    }));

    content.appendChild(createElement('p', {
        className: 'cid-wizard-text',
        textContent: 'After several rotations, claim keys carry multi-layer encryption '
            + 'envelopes that slow down decryption. Flattening collapses every claim key back '
            + 'to a single layer encrypted with the current attr_encrypt key. Old key bag '
            + 'entries and wrapped org keys are kept for replication safety (per §3.4.8).',
    }));

    const statusEl = createElement('div', { className: 'cid-trust-success' });
    const errorEl = createElement('div', { className: 'cid-pane-error' });
    content.appendChild(errorEl);
    content.appendChild(statusEl);

    const flattenBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes',
        textContent: 'Flatten Now',
    });
    flattenBtn.style.marginTop = '12px';

    flattenBtn.addEventListener('click', async () => {
        flattenBtn.setAttribute('disabled', '');
        errorEl.classList.remove('cid-pane-error--visible');

        try {
            // 1. Pull the work-list of chained (keyIndex > 0) claim keys.
            statusEl.textContent = 'Loading chained claim keys...';
            const chained = await organizationApi.listChainedClaimKeys(orgCid);
            if (chained.length === 0) {
                statusEl.textContent = 'Nothing to flatten — all claim keys are already at depth 1.';
                setTimeout(() => modal.close(), 2000);
                return;
            }

            // 2. Unwrap our wrapped org keys to get the org's full attr_encrypt
            //    private-key chain. As an owner we hold every generation we were
            //    onboarded with, which is the chain needed to peel envelopes.
            statusEl.textContent = 'Unwrapping organization key chain...';
            const mySecretKey = keys.getPrivateKey('attr_encrypt');
            if (!mySecretKey) throw new Error('Own attr_encrypt key not loaded');
            const myWrappedKeys = await delegateApi.getWrappedOrgKeys();

            const attrChain: { secretKey: Uint8Array; keyIndex: number }[] = [];
            for (const wk of myWrappedKeys) {
                if (wk.keyId !== 'attr_encrypt') continue;
                const privKeyB64 = await unwrapPrivateKey(wk.wrappedKey, aes.fromBase64(mySecretKey));
                attrChain.push({ secretKey: aes.fromBase64(privKeyB64), keyIndex: wk.keyIndex });
            }
            if (attrChain.length === 0) throw new Error('No attr_encrypt keys available — re-onboard required');

            // 3. Pull the org's current active attr_encrypt PUBLIC key — we re-wrap
            //    each chained blob to it. We don't need its keyIndex on the wire:
            //    flattened blobs are stored with keyIndex=0 (the "single layer,
            //    decrypt with currently-active key" sentinel — same convention as
            //    fresh shares; see the server-side comment on FlattenOrgKeys).
            //    The cross-CID public-keys endpoint (/api/account/publickeys/{cid})
            //    is already pre-filtered to active keys server-side and does NOT
            //    return a `status` field — so we must not filter on it here.
            statusEl.textContent = 'Resolving current attr_encrypt generation...';
            const orgPubKeys = await accountApi.getPublicKeys(orgCid);
            const currentAttrPub = orgPubKeys
                .filter(k => k.keyId === 'attr_encrypt')
                .sort((a, b) => b.keyIndex - a.keyIndex)[0];
            if (!currentAttrPub) throw new Error('Org has no active attr_encrypt key');
            const currentPubKeyBytes = aes.fromBase64(currentAttrPub.publicKey);

            // 4. For each chained claim key: chain-walk to recover the bare 32-byte
            //    symmetric key, re-wrap with only the current public key. Store at
            //    keyIndex=0 — the decryption path treats this as a single-layer blob
            //    decryptable with the active key (claim-detail-pane.ts:122).
            statusEl.textContent = `Flattening ${chained.length} claim key(s)...`;
            const flattenedKeys: { claimUuid: string; encryptedKey: string; keyIndex: number }[] = [];
            for (const ck of chained) {
                const reEncrypted = await reEncryptClaimKeyWithChain(
                    ck.encryptedKey,
                    attrChain,
                    ck.keyIndex,
                    currentPubKeyBytes,
                );
                flattenedKeys.push({
                    claimUuid: ck.claimUuid,
                    encryptedKey: reEncrypted,
                    keyIndex: 0,
                });
            }

            // 5. Submit the batch. Server validates each row against the current
            //    keyIndex and updates claimkey.encryptedkey + keyIndex inside one tx.
            statusEl.textContent = 'Submitting flattened keys...';
            const result = await organizationApi.flattenOrgKeys(orgCid, { flattenedKeys });

            statusEl.textContent = `Flattened ${result.flattened} claim key(s).`;
            shell.emit('data-changed', { type: 'claims' });
            setTimeout(() => modal.close(), 2000);
        } catch (e) {
            statusEl.textContent = '';
            errorEl.textContent = e instanceof Error ? e.message : 'Flatten failed';
            errorEl.classList.add('cid-pane-error--visible');
            flattenBtn.removeAttribute('disabled');
        }
    });

    content.appendChild(flattenBtn);
    const modal = shell.showModal(content);
}
